import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Box,
  Button,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React from 'react';
import type { HolderType } from '../../data/types';
import { money } from '../../lib/format';
import { HOLDER_TYPE_LABELS } from '../../lib/labels';
import { useInvestorScope } from '../../store/selectors';
import { shortEntityName } from '../common';
import type { StepProps } from './applyTypes';

export function InvestmentStep({ form, update }: StepProps) {
  const scope = useInvestorScope();
  const openFunds = scope.funds.filter(
    (fund) => fund.status === 'RAISING' || fund.status === 'OPEN',
  );
  const classes = scope.classes.filter(
    (unitClass) => unitClass.fundId === form.fundId && unitClass.open,
  );
  const unitClass = scope.classes.find((item) => item.id === form.classId);
  const amount = Number(form.amount) || 0;
  return (
    <Stack spacing={2}>
      <TextField
        select
        label="Fund"
        value={form.fundId}
        onChange={(event) =>
          update({
            fundId: event.target.value,
            classId:
              scope.classes.find((item) => item.fundId === event.target.value && item.open)?.id ??
              '',
          })
        }
      >
        {openFunds.map((fund) => (
          <MenuItem key={fund.id} value={fund.id}>
            {fund.name}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Unit class"
        value={form.classId}
        onChange={(event) => update({ classId: event.target.value })}
      >
        {classes.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name} · target {item.targetReturnPct}% p.a. · min {money(item.minInvestment)}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Amount (AUD)"
        value={form.amount}
        onChange={(event) => update({ amount: event.target.value.replace(/[^\d]/g, '') })}
        helperText={
          unitClass && amount > 0 && amount < unitClass.minInvestment
            ? `Minimum is ${money(unitClass.minInvestment)}`
            : amount
              ? money(amount)
              : undefined
        }
        error={Boolean(unitClass && amount > 0 && amount < unitClass.minInvestment)}
      />
      <TextField
        select
        label="Invest through"
        value={form.holderId}
        onChange={(event) => {
          const holder = scope.holders.find((item) => item.id === event.target.value);
          update(
            holder
              ? {
                  holderId: holder.id,
                  applicantName: holder.name,
                  applicantType: holder.type,
                  contactName: holder.contactName,
                  email: holder.email,
                  abn: holder.abn ?? '',
                  residency: holder.residency,
                  bankName: holder.bank.accountName,
                  bsb: holder.bank.bsb,
                  account: holder.bank.accountNumber,
                  owners: holder.beneficialOwners
                    .filter((owner) => owner.role !== 'BENEFICIARY')
                    .map((owner) => ({ name: owner.name, role: owner.role.toLowerCase() })),
                }
              : { holderId: 'NEW', applicantName: '', abn: '', owners: [] },
          );
        }}
      >
        {scope.holders.map((holder) => (
          <MenuItem key={holder.id} value={holder.id}>
            {shortEntityName(holder)} (existing)
          </MenuItem>
        ))}
        <MenuItem value="NEW">A new entity</MenuItem>
      </TextField>
    </Stack>
  );
}

export function EntityStep({ form, update, existingHolder }: StepProps) {
  return (
    <Stack spacing={2}>
      {existingHolder && (
        <Alert severity="success">
          We&apos;ve pre-filled these details from your existing account. Check they&apos;re still
          correct.
        </Alert>
      )}
      <TextField
        select
        label="Entity type"
        value={form.applicantType}
        onChange={(event) => update({ applicantType: event.target.value as HolderType })}
        disabled={existingHolder}
      >
        {(Object.keys(HOLDER_TYPE_LABELS) as HolderType[]).map((type) => (
          <MenuItem key={type} value={type}>
            {HOLDER_TYPE_LABELS[type]}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Full registered name"
        value={form.applicantName}
        onChange={(event) => update({ applicantName: event.target.value })}
        helperText={
          form.applicantType === 'TRUST' || form.applicantType === 'SMSF'
            ? 'e.g. Trustee Pty Ltd ATF Family Trust'
            : undefined
        }
      />
      {form.applicantType !== 'INDIVIDUAL' && (
        <TextField
          label="ABN"
          value={form.abn}
          onChange={(event) => update({ abn: event.target.value })}
        />
      )}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
        <TextField
          label="Contact name"
          value={form.contactName}
          onChange={(event) => update({ contactName: event.target.value })}
        />
        <TextField
          label="Email"
          value={form.email}
          onChange={(event) => update({ email: event.target.value })}
        />
      </Box>
    </Stack>
  );
}

export function OwnersStep({ form, update }: StepProps) {
  if (form.applicantType === 'INDIVIDUAL') {
    return (
      <Alert severity="info">
        You&apos;re investing as an individual, so there are no other owners or controllers to add.
      </Alert>
    );
  }
  const setOwner = (index: number, patch: Partial<{ name: string; role: string }>) =>
    update({
      owners: form.owners.map((owner, position) =>
        position === index ? { ...owner, ...patch } : owner,
      ),
    });
  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Add every director, trustee, partner and anyone who owns 25% or more. Each person gets a
        link to verify their identity on their own phone.
      </Typography>
      {form.owners.map((owner, index) => (
        <Stack key={index} direction="row" spacing={1} alignItems="center">
          <TextField
            label="Full name"
            value={owner.name}
            onChange={(event) => setOwner(index, { name: event.target.value })}
            sx={{ flex: 2 }}
          />
          <TextField
            select
            label="Role"
            value={owner.role}
            onChange={(event) => setOwner(index, { role: event.target.value })}
            sx={{ flex: 1 }}
          >
            {['director', 'trustee', 'shareholder', 'partner', 'appointor'].map((role) => (
              <MenuItem key={role} value={role}>
                {role}
              </MenuItem>
            ))}
          </TextField>
          <IconButton
            aria-label="Remove person"
            onClick={() =>
              update({ owners: form.owners.filter((_owner, position) => position !== index) })
            }
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Stack>
      ))}
      <Box>
        <Button
          startIcon={<AddIcon />}
          onClick={() => update({ owners: [...form.owners, { name: '', role: 'director' }] })}
        >
          Add person
        </Button>
      </Box>
    </Stack>
  );
}
